'use client'
import { cn } from '@/utils/ui'
import Link from 'next/link'
import React from 'react'

import type { Article, ButtonField } from '@/payload-types'
import type { TypedLocale } from 'payload'

import { CardType } from '@/ui/components/Card/types'
import { StandardCard, StandardCardProps } from '@/ui/components/Card/StandardCard'
import {
  StandardClientCard,
  StandardClientCardProps,
} from '@/ui/components/Card/StandardCard/index.client'
import { EventCard, EventCardProps } from '@/ui/components/Card/EventCard'
import { ProcessCard, ProcessCardProps } from '@/ui/components/Card/ProcessCard'
import { TeaserCard, TeaserCardProps } from '@/ui/components/Card/TeaserCard'
import { LinkCard, LinkCardProps } from '@/ui/components/Card/LinkCard'
import { CoreStageCard, CoreStageCardProps } from './CoreStageCard'
import { NoteInfoCard, NoteInfoCardProps } from './NoteInfoCard'
import { NewsCard, NewsCardProps } from './NewsCard'
import { TrendingCard, TrendingCardProps } from './TrendingCard'
import { ResourceCard, ResourceCardProps } from './ResourceCard'
import { ResourceDownloadCard, ResourceDownloadCardProps } from './ResourceDownloadCard'

export type CardPostData = Pick<Article, 'slug' | 'meta' | 'title'>

export type BaseCardProps = {
  type: CardType
  href?: string | null
  className?: string
  target?: '_blank' | '_self'
  isExternal?: boolean
  randomSort?: number // Used for sorting merged products/solutions
  button?: ButtonField | null
  locale?: TypedLocale
}

export type CardProps =
  | ({ type: CardType.Event } & BaseCardProps & EventCardProps)
  | ({ type: CardType.Process } & BaseCardProps & ProcessCardProps)
  | ({ type: CardType.Teaser } & BaseCardProps & TeaserCardProps)
  | ({ type: CardType.Link } & BaseCardProps & LinkCardProps)
  | ({ type: CardType.Standard } & BaseCardProps & StandardCardProps)
  | ({ type: CardType.CoreStage } & BaseCardProps & CoreStageCardProps)
  | ({ type: CardType.News } & BaseCardProps & NewsCardProps)
  | ({ type: CardType.Trending } & BaseCardProps & TrendingCardProps)
  | ({ type: CardType.Resource } & BaseCardProps & ResourceCardProps)
  | ({ type: CardType.ResourceDownload } & BaseCardProps & ResourceDownloadCardProps)
  | ({ type: CardType.StandardFavorite } & BaseCardProps &
      StandardCardProps &
      StandardClientCardProps)
export const Card: React.FC<CardProps> = (props) => {
  const { type, className, href, target } = props

  //const hasCategories = categories && Array.isArray(categories) && categories.length > 0

  const renderCard = () => {
    switch (type) {
      case CardType.Event:
        return <EventCard {...props} />
      case CardType.Process:
        return <ProcessCard {...props} />
      case CardType.Teaser:
        return <TeaserCard {...props} />
      case CardType.Link:
        return <LinkCard {...props} />
      case CardType.StandardFavorite:
        return <StandardClientCard {...props} />
      case CardType.CoreStage:
        return <CoreStageCard {...props} />
      case CardType.News:
        return (
          <NewsCard
            showLinkIcon={
              (props.href !== undefined && props.href !== null) ||
              (props.button?.linkType === 'popup' && props.button?.popupbutton !== undefined)
            }
            {...props}
          />
        )
      case CardType.Trending:
        return <TrendingCard {...props} />
      case CardType.Resource:
        return <ResourceCard {...props} />
      case CardType.ResourceDownload:
        return <ResourceDownloadCard {...props} />
      case CardType.Standard:
      default:
        return <StandardCard {...props} />
    }
  }

  const linkProps = target === '_blank' ? { target: '_blank', rel: 'noopener noreferrer' } : {}

  // Event cards have their own click handling (popover), so don't wrap them in a Link
  if (type === CardType.Event) {
    return renderCard()
  }

  // ResourceCard and ResourceDownloadCard handle their own navigation via button, so don't wrap them in a Link
  if (type === CardType.Resource || type === CardType.ResourceDownload) {
    return renderCard()
  }

  // NewsCard with popup button handles its own click handling, so don't wrap it in a Link
  if (type === CardType.News && props.button?.linkType === 'popup') {
    return renderCard()
  }

  return href ? (
    <Link href={href} className={cn('hover:cursor-pointer flex flex-col', className)} {...linkProps}>
      {renderCard()}
    </Link>
  ) : (
    renderCard()
  )
}
